// Track 1 — Rapha access guard.
//
// Two independent gates:
//   1. `/rapha/fit` and `/rapha/catalog` are Rapha-member tools that
//      live on the Rapha subdomain. If a visitor lands on either path
//      while NOT on rapha.aero.chat, redirect to the homepage — those
//      tools shouldn't leak onto www.aero.chat regardless of role.
//   2. On rapha.aero.chat, the entire site (with the public allowlist)
//      requires role='rapha'; non-members see the friendly "members only"
//      card instead of the full app.
//
// Public routes (login, register, magic link, password reset, the public
// homepage) are bypassed via the allowlist so non-rapha users still have
// a way to authenticate before we can check their role.

import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { isRaphaDomain } from '../services/subdomain';
import { useCurrentUser } from '../services/useCurrentUser';
import RaphaMembersOnly from './RaphaMembersOnly';

const PUBLIC_PATH_PREFIXES = [
  '/login',
  '/register',
  '/password',
  '/verify',
  '/privacy',
  '/terms',
  '/liability',
  '/rapha/register',
  '/rapha/welcome',
  // Wave 20 — Drop Links are public tokenized URLs. Renders without
  // auth on both www.aero.chat and rapha.aero.chat.
  '/shared/',
  '/peak/',
  '/dropped/',
];

/** Paths that only exist under rapha.aero.chat. Hitting them on any
 *  other subdomain (including www.aero.chat) redirects to `/`. */
const RAPHA_SUBDOMAIN_ONLY_PATH_PREFIXES = [
  '/rapha/fit',
  '/rapha/catalog',
];

interface Props {
  children: React.ReactNode;
}

export default function RaphaGuard({ children }: Props) {
  const { pathname } = useLocation();
  const user = useCurrentUser();
  const onRapha = isRaphaDomain();

  // Rapha-subdomain-only paths: if the visitor isn't on rapha.aero.chat,
  // send them home. These tools deliberately don't exist on the public
  // domain even for Rapha members — Rapha work happens on the Rapha
  // subdomain.
  const isRaphaSubdomainOnlyPath = RAPHA_SUBDOMAIN_ONLY_PATH_PREFIXES.some(
    (p) => pathname.startsWith(p),
  );
  if (isRaphaSubdomainOnlyPath && !onRapha) {
    return <Navigate to="/" replace />;
  }

  if (!onRapha) {
    return <>{children}</>;
  }

  // Public paths on the rapha subdomain stay accessible — riders need to
  // be able to log in / register before we can check their role.
  if (PUBLIC_PATH_PREFIXES.some((p) => pathname.startsWith(p))) {
    return <>{children}</>;
  }

  if (!user.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'rapha') {
    return <RaphaMembersOnly />;
  }

  return <>{children}</>;
}
